import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

/** Navigation state that opens `/chat` with a first message (Home and space composers, quick actions). */
export interface InitialPromptState {
  /** Sent as soon as the new chat can send. */
  prompt?: string;
  /** Left in the message box for the user to finish and send. */
  prefill?: string;
}

/**
 * Sends the prompt handed over in navigation state once, as soon as the new chat can send
 * (`ready`: a model or agent is resolved). Only for a new chat. Once per history entry: going Back to it doesn't
 * send it again, while another new chat opened with a prompt (the page stays mounted between chats) sends its own.
 */
export function useInitialPrompt(isNewChat: boolean, ready: boolean, send: (prompt: string) => void) {
  const location = useLocation();
  const prompt = isNewChat ? (location.state as InitialPromptState | null)?.prompt?.trim() : undefined;
  const sentFor = useRef(new Set<string>());
  const entry = location.key;
  useEffect(() => {
    if (!prompt || !ready || sentFor.current.has(entry)) return;
    sentFor.current.add(entry);
    send(prompt);
  }, [prompt, ready, send, entry]);
}

/** The text handed over in navigation state for the message box to start with. */
export function useInitialPrefill(): string {
  const location = useLocation();
  return (location.state as InitialPromptState | null)?.prefill ?? "";
}
